import "./statistics-topper.scss";

import cn from "clsx";
import {
  CtaList,
  CtaListProps,
} from "design-system/components/primitives/cta-list/cta-list";
import {
  MediaAsset,
  MediaAssetProps,
} from "design-system/components/primitives/media-asset/media-asset";
import {
  Statistic,
  StatisticProps,
} from "design-system/components/primitives/statistic/statistic";
import { SimpleTheme } from "design-system/utils/theme";
import { ReactNode } from "react";
import { EditAttributes } from "design-system/types/types";
import { shouldDecreaseTextSize } from "design-system/utils/text";

export interface StatisticsTopperProps {
  asset?: MediaAssetProps;
  cta?: CtaListProps;
  stats?: Array<StatisticProps>;
  subheading?: ReactNode;
  title?: ReactNode;
  theme?: SimpleTheme;

  editAttributes?: {
    title?: EditAttributes;
    subtitle?: EditAttributes;
  };
}

/**
 * ## See it in use on...
 * - A [landing page](/story/example-pages-landing-pages-statistics-topper--story)
 *
 *  - **`id: CT-001-005-00`**
 *  - **`data-region: ct__page__statistics`**
 */
export function StatisticsTopper({
  asset,
  cta,
  stats,
  subheading,
  title,
  theme,
  editAttributes,
}: StatisticsTopperProps) {
  if (!Array.isArray(stats)) stats = [];

  if (asset?.image) asset.image.loading = "eager";

  const classNameVariant = !asset ? "-text-only" : "";

  const headingClass = cn(
    `hbs-statistics-topper__title${classNameVariant}`,
    typeof title === "string"
      ? shouldDecreaseTextSize(title) && "hbs-statistics-topper__title--long"
      : undefined,
  );

  return (
    <div
      className="hbs-topper-wrapper hbs-statistics-topper-wrapper"
      data-theme={theme}
      data-region="ct__page__statistics"
    >
      <div className="hbs-topper hbs-statistics-topper">
        <div className={`hbs-statistics-topper__content${classNameVariant}`}>
          {title && (
            <h1 {...editAttributes?.title} className={headingClass}>
              {title}
            </h1>
          )}

          {subheading && (
            <div
              {...editAttributes?.subtitle}
              className="hbs-statistics-topper__subheading"
            >
              {subheading}
            </div>
          )}

          {cta && (
            <div className="hbs-statistics-topper__cta-list">
              <CtaList {...cta} />
            </div>
          )}
        </div>

        {asset && (
          <div className="hbs-statistics-topper__media">
            <MediaAsset {...asset} />
          </div>
        )}

        <div className={`hbs-statistics-topper__statistics${classNameVariant}`}>
          {stats.map((stat, i) => (
            <div
              key={i}
              className={`hbs-statistics-topper__statistics-item${classNameVariant}`}
            >
              <Statistic {...stat} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
